"use client";

import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/toggle-group";
import { Loader2, RefreshCw, SunMedium } from "lucide-react";
import { toast } from "sonner";
import {
  backendErrorMessage,
  useCameraExposure,
  useSetExposure,
} from "./api";
import { useLocalization } from "@/context/localization-context";

export const ExposureCard = ({ deviceId }: { deviceId: string }) => {
  const { t, formatNumber } = useLocalization();
  const exposure = useCameraExposure(deviceId);
  const setExposure = useSetExposure(deviceId);

  const [mode, setMode] = useState<"auto" | "manual">("auto");
  const [gain, setGain] = useState(1.0);
  const [exposureUs, setExposureUs] = useState(10000);

  // Seed the manual inputs from the live values whenever they load
  useEffect(() => {
    if (!exposure.data) return;
    setMode(exposure.data.ae_mode);
    if (exposure.data.analogue_gain != null) {
      setGain(Number(exposure.data.analogue_gain.toFixed(2)));
    }
    if (exposure.data.exposure_time != null) {
      setExposureUs(Math.round(exposure.data.exposure_time));
    }
  }, [exposure.data]);

  const apply = async () => {
    try {
      await setExposure.mutateAsync(
        mode === "auto"
          ? { mode: "auto" }
          : { mode: "manual", analogue_gain: gain, exposure_time_us: exposureUs },
      );
      toast.success(t("cameras.exposure.applied"));
    } catch (error) {
      toast.error(t("cameras.exposure.failed", { error: backendErrorMessage(error) }));
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <SunMedium className="h-4 w-4" />
            {t("cameras.exposure.title")}
          </CardTitle>
          <div className="flex items-center gap-2">
            {exposure.data && (
              <Badge variant="outline" className="font-mono text-xs">
                {t("cameras.exposure.liveValues", {
                  mode:
                    exposure.data.ae_mode === "auto"
                      ? t("cameras.control.modeAuto")
                      : exposure.data.ae_mode === "manual"
                        ? t("cameras.control.modeManual")
                        : exposure.data.ae_mode,
                  gain:
                    exposure.data.analogue_gain != null
                      ? formatNumber(exposure.data.analogue_gain, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "?",
                  time:
                    exposure.data.exposure_time != null
                      ? formatNumber(exposure.data.exposure_time)
                      : "?",
                })}
              </Badge>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => exposure.refetch()}
              disabled={exposure.isFetching}
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${exposure.isFetching ? "animate-spin" : ""}`}
              />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={mode}
          onValueChange={(value) => value && setMode(value as "auto" | "manual")}
          className="justify-start"
        >
          <ToggleGroupItem value="auto">{t("cameras.control.modeAuto")}</ToggleGroupItem>
          <ToggleGroupItem value="manual">{t("cameras.control.modeManual")}</ToggleGroupItem>
        </ToggleGroup>

        {mode === "manual" && (
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">{t("cameras.exposure.gain")}</Label>
                <Input
                  type="number"
                  min={1}
                  max={16}
                  step={0.1}
                  value={gain}
                  onChange={(e) => setGain(Number(e.target.value))}
                  className="h-7 w-24 text-right"
                />
              </div>
              <Slider
                min={1}
                max={16}
                step={0.1}
                value={[gain]}
                onValueChange={([value]) => setGain(value)}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">{t("cameras.exposure.time")}</Label>
                <Input
                  type="number"
                  min={100}
                  max={200000}
                  step={100}
                  value={exposureUs}
                  onChange={(e) => setExposureUs(Number(e.target.value))}
                  className="h-7 w-28 text-right"
                />
              </div>
              <Slider
                min={100}
                max={100000}
                step={100}
                value={[Math.min(exposureUs, 100000)]}
                onValueChange={([value]) => setExposureUs(value)}
              />
            </div>
          </div>
        )}

        <Button
          size="sm"
          onClick={apply}
          disabled={setExposure.isPending}
          className="w-full"
        >
          {setExposure.isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          {t("cameras.exposure.apply")}
        </Button>
      </CardContent>
    </Card>
  );
};
